import { addressLike } from "@gearbox-protocol/cli-utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from "@gearbox-protocol/deploy-tools-ui";
import {
  createAnvilClient,
  lowerHealthFactor,
} from "@gearbox-protocol/sdk/dev";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FC } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useUnpausedForkStatus } from "routes/fork/context";
import { useSDK } from "routes/fork/sdk-context";
import type { Address } from "viem";
import { http } from "viem";
import { z } from "zod";

import logger from "../../logger";

const schema = z.object({
  creditAccount: addressLike(),
  hf: z.coerce.bigint<string>().positive(),
});

type LowerHealthFactorInput = z.input<typeof schema>;
type LowerHealthFactorSchema = z.output<typeof schema>;

interface LowerHealthFactorDialogProps {
  toggleOpen: (open: boolean) => void;
}

const LowerHealthFactorDialog: FC<LowerHealthFactorDialogProps> = ({
  toggleOpen,
}) => {
  const { toast } = useToast();
  const { rpcUrl } = useUnpausedForkStatus();
  const { value: sdk } = useSDK();

  const form = useForm<
    LowerHealthFactorInput,
    unknown,
    LowerHealthFactorSchema
  >({
    resolver: zodResolver(schema) as Resolver<
      LowerHealthFactorInput,
      unknown,
      LowerHealthFactorSchema
    >,
    defaultValues: {
      creditAccount: "" as string as Address,
      hf: "9990",
    },
  });

  const submit = form.handleSubmit(async ({ creditAccount, hf }) => {
    if (!sdk) {
      return;
    }
    try {
      const ca = await sdk.accounts.getCreditAccountData(creditAccount);
      if (!ca) {
        throw new Error(`credit account ${creditAccount} not found`);
      }
      const anvil = createAnvilClient({
        chain: sdk.chain,
        transport: http(rpcUrl, { timeout: 240_000 }),
      });
      const { account } = await lowerHealthFactor(anvil, sdk, ca, {
        hf,
        logger,
      });
      // setLTs does not wait for receipts; force-mine so that
      // LT update events are included in a block before syncState scans logs
      await anvil.mine({ blocks: 1 });
      toast({
        variant: "success",
        title: "Health factor lowered",
        description: `${creditAccount}: ${ca.healthFactor} → ${account.healthFactor}`,
      });
      const block = await sdk.client.getBlock({ blockTag: "latest" });
      await sdk.syncState({
        blockNumber: block.number,
        timestamp: block.timestamp,
        ignoreUpdateablePrices: true,
      });
      toggleOpen(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to lower health factor",
        description: `${e}`,
      });
    }
  });

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={true} onOpenChange={toggleOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Lower HF</DialogTitle>
          <DialogDescription>
            Lower liquidation thresholds of a credit account's credit manager,
            so that the account's health factor drops to the target
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} className="space-y-2">
            <FormField
              control={form.control}
              name="creditAccount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credit account</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="0x..."
                      autoComplete="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="hf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target health factor (bps)</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" spellCheck={false} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {submitting && (
          <p className="text-muted-foreground text-sm">
            Lowering health factor, this may take a while...
          </p>
        )}

        <DialogFooter>
          <Button
            type="submit"
            disabled={submitting || !sdk}
            loading={submitting}
            onClick={submit}
          >
            Lower HF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LowerHealthFactorDialog;
